import { handleActions } from 'redux-actions'
import { createSelector } from 'reselect'
import { combineReducers } from 'redux'
import { selectorForSlice, unsetState } from 'lp-redux-utils'
import { handleSuccess, setOnSuccess } from 'lp-redux-api'
import { find, get } from 'lodash'
import { set } from 'lodash/fp'
import {
  reducer as membershipCardReducer,
  reducerKey as membershipCardReducerKey,
} from './membership_card'
import { reducer as boatsReducer, reducerKey as boatsReducerKey } from './boats'
import {
  reducer as renewalReducer,
  reducerKey as renewalReducerKey,
} from './renewal'
import * as actions from './actions'
import * as apiActions from 'api-actions'
import { selectors as memberSelectors } from 'member-reducer'
import { BOAT_STATUS, PRIMARY_CONTACT } from 'types'
import { findPrimaryBoat } from 'utils'
import { mergeMatchingContact } from 'utils/mergeContactInContacts'

const reducerKey = 'memberships'
const slice = 'root.memberPortal.memberships.allMemberships'

const initialState = {}

const membershipsReducer = handleActions(
  {
    [actions.clearAllMemberships]: unsetState('allMemberships'),
    [apiActions.fetchAllMembershipsForAccount]: setOnSuccess('allMemberships'),
    [apiActions.updateMembershipApplicationAccount]: handleSuccess(
      (state, { payload: { data } }) => {
        const existingAccount = state.allMemberships?.account
        const updatedAccount = data?.account
        if (!existingAccount || !updatedAccount) return state
        // Welcome / SidePanel read this account (business name, address).
        // Same heroku-id + skip-null merge as login contacts.
        return set(
          'allMemberships.account',
          mergeMatchingContact(existingAccount, updatedAccount),
          state
        )
      }
    ),
  },
  initialState
)

const select = selectorForSlice(slice)

const selectors = {
  allMemberships: select('allMemberships'),
}

selectors.account = createSelector(
  [selectors.allMemberships],
  (allMemberships) => {
    if (!allMemberships) return
    return allMemberships.account
  }
)

selectors.stripeCustomerId = createSelector(
  [selectors.account],
  (account) => {
    if(!account) return 
    return get(account, 'stripe_customer.stripe_customer_id__c', null)
  }
)

selectors.lastFourDigits = createSelector(
  [selectors.account, memberSelectors.membership],
  (account, membership) => {
    const defaultPaymentMethod = find(
      account?.payment_methods,
      (pm) => pm.pymt__default__c === true
    )
    return (
      defaultPaymentMethod?.pymt__last_4_digits__c ??
      membership?.last_4_digits__c ??
      null
    )
  }
)

// Aliased for naming parity with the other prefill selectors.
selectors.applicationPrefillAccount = selectors.account

selectors.applicationPrefillContact = createSelector(
  [selectors.allMemberships],
  (allMemberships) => {
    if (!allMemberships?.contacts) return
    return allMemberships.contacts.find(
      (c) => c.contact_type__c === PRIMARY_CONTACT
    )
  }
)

// Surfaces the existing membership's heroku id for the post-login PATCH.
selectors.applicationPrefillMembership = createSelector(
  [selectors.allMemberships],
  (allMemberships) => {
    if (!allMemberships?.memberships) return
    return allMemberships.memberships.find((m) => m.primary_membership__c)
  }
)

selectors.primaryBoat = createSelector(
  [memberSelectors.membership],
  (membership) => {
    if (!membership) return
    return findPrimaryBoat(membership.boats)
  }
)

selectors.additionalBoats = createSelector(
  [memberSelectors.membership],
  (membership) => {
    if (!membership) return
    return membership.boats.filter(
      ({ boat_status__c, primary_boat__c }) =>
        boat_status__c === BOAT_STATUS.ACTIVE && primary_boat__c === false
    )
  }
)

const reducer = combineReducers({
  allMemberships: membershipsReducer,
  [boatsReducerKey]: boatsReducer,
  [membershipCardReducerKey]: membershipCardReducer,
  [renewalReducerKey]: renewalReducer,
})

export { reducer, selectors, reducerKey }
